import React, { FC, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Box } from "@mui/system";
import { Dialog, EsgSingleSelectControlledField, BasicButton } from "components";

import { SiteByIdDTO, UserDTO, addDataProvider } from "api";

import { useNotify } from "hooks";

import { QUERY_KEYS } from "consts";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: UserDTO[];
  site: SiteByIdDTO;
}

export const UserModal: FC<UserModalProps> = ({
  isOpen,
  onClose,
  users,
  site,
}) => {
  const notify = useNotify();

  const [values, setValues] = useState<any>({});
  const [availableDataProviders, setAvailableDataProviders] = useState(() => {
    if (Array.isArray(users)) {
      return users.map((u) => ({
        label: `${u.firstName || ""} ${u.lastName || ""}`,
        value: u.id,
      }));
    }
    return [];
  });

  const useAddDataProvider = () => {
    const queryClient = useQueryClient();

    const mutationFn = ({ siteId, userId }: { siteId: number; userId: number }) =>
      addDataProvider(siteId, userId);

    return useMutation(mutationFn, {
      onSuccess: () => {
        notify.success('Data provider successfully added!');
        queryClient.invalidateQueries([QUERY_KEYS.SITES]);
      },
      onError: () => {
        notify.error('Some error has occurred while adding the data provider!');
      },
    });
  };

  const { mutate: addDataProviderRequest } = useAddDataProvider();

  const handleAddProvider = async () => {
    const selectedDataProviderId = values["Data Provider"];

    addDataProviderRequest(
      {
        siteId: site?.id,
        userId: selectedDataProviderId,
      },
      {
        onSuccess: () => {
          setAvailableDataProviders((prevDataProviders) =>
            prevDataProviders.filter(
              (provider: any) => provider.value !== selectedDataProviderId
            )
          );
        },
      }
    );
    setValues({});
    onClose();
  };

  const handleClose = () => {
    setValues({});
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      dividers
      onClose={onClose}
      title="Add Data Provider"
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <BasicButton onClick={handleClose}>Cancel</BasicButton>
          <BasicButton
            onClick={handleAddProvider}
            disabled={!values["Data Provider"]}
          >
            Add
          </BasicButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          <form onSubmit={(e) => e.preventDefault()}>
            <EsgSingleSelectControlledField
              label="Data Provider"
              selectedValue={values["Data Provider"] || ""}
              setSelectedValue={(value) => {
                setValues({
                  ...values,
                  "Data Provider": value,
                });
              }}
              options={availableDataProviders}
              placeholder="Select a data provider"
              width="500px"
              allowDeselect
            />
          </form>
        </Box>
      </Box>
    </Dialog>
  );
};
